
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import SEOHead from '../../components/SEOHead';

export default function Help() {
  const [activeCategory, setActiveCategory] = useState<'geral' | 'projetos' | 'tarefas' | 'conta'>('geral');
  const [searchQuery, setSearchQuery] = useState('');
  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const navigate = useNavigate();

  const faqs = {
    geral: [
      {
        question: 'Como começar a usar o Vibecode?',
        answer: 'Para começar, faça seu cadastro na plataforma, confirme seu email e acesse o dashboard. Você pode criar seu primeiro projeto clicando em "Criar Novo Projeto" e seguir o assistente de configuração.'
      },
      {
        question: 'O Vibecode é gratuito?',
        answer: 'Oferecemos um plano gratuito com recursos básicos para até 3 projetos. Para recursos avançados e projetos ilimitados, temos planos pagos a partir de R$ 29,90/mês.'
      },
      {
        question: 'Posso usar o Vibecode em dispositivos móveis?',
        answer: 'Sim! O Vibecode é totalmente responsivo e funciona perfeitamente em smartphones e tablets através do navegador. Em breve teremos aplicativos nativos para iOS e Android.'
      },
      {
        question: 'Como posso convidar membros para minha equipe?',
        answer: 'No dashboard do projeto, clique em "Gerenciar Equipe" e depois em "Convidar Membros". Digite os emails dos colaboradores e eles receberão um convite por email.'
      }
    ],
    projetos: [
      {
        question: 'Como criar um novo projeto?',
        answer: 'No dashboard principal, clique no botão "Criar Novo Projeto". Preencha as informações básicas como título, descrição, datas e responsável. Você pode adicionar mais detalhes depois.'
      },
      {
        question: 'Posso duplicar um projeto existente?',
        answer: 'Sim! Na página de detalhes do projeto, clique no menu de ações e selecione "Duplicar Projeto". Isso criará uma cópia com todas as configurações, mas sem as tarefas concluídas.'
      },
      {
        question: 'Como arquivar um projeto?',
        answer: 'Vá até as configurações do projeto e clique em "Arquivar Projeto". Projetos arquivados não aparecem no dashboard principal, mas podem ser acessados através do filtro "Arquivados".'
      },
      {
        question: 'Posso definir diferentes níveis de acesso para os membros?',
        answer: 'Sim! Você pode definir membros como Administradores, Editores ou Visualizadores, cada um com diferentes permissões dentro do projeto.'
      }
    ],
    tarefas: [
      {
        question: 'Como criar uma tarefa?',
        answer: 'Dentro de um projeto, vá para a aba "Tarefas" e clique em "Nova Tarefa". Preencha o título, descrição, responsável, prazo e prioridade.'
      },
      {
        question: 'Posso criar subtarefas?',
        answer: 'Sim! Dentro de uma tarefa, você pode adicionar subtarefas para dividir o trabalho em partes menores e mais gerenciáveis.'
      },
      {
        question: 'Como recebo notificações sobre tarefas?',
        answer: 'Você receberá notificações por email e no sistema quando for atribuído a uma tarefa, quando houver comentários ou quando o prazo estiver próximo. Configure suas preferências no perfil.'
      },
      {
        question: 'Posso anexar arquivos às tarefas?',
        answer: 'Sim! Você pode anexar documentos, imagens e outros arquivos diretamente nas tarefas. Suportamos arquivos de até 10MB por anexo.'
      }
    ],
    conta: [
      {
        question: 'Como alterar minha senha?',
        answer: 'Vá para seu perfil, clique na aba "Configurações" e depois em "Alterar Senha". Digite sua senha atual e a nova senha duas vezes.'
      },
      {
        question: 'Como cancelar minha conta?',
        answer: 'Entre em contato conosco através do formulário de suporte. Processaremos sua solicitação em até 48 horas e todos os seus dados serão removidos permanentemente.'
      },
      {
        question: 'Posso exportar meus dados?',
        answer: 'Sim! Você pode exportar todos os seus projetos e dados em formato PDF ou Excel através das configurações da conta.'
      },
      {
        question: 'Como ativar a autenticação de dois fatores?',
        answer: 'No seu perfil, vá para "Configurações" > "Segurança" e clique em "Ativar 2FA". Você precisará de um aplicativo autenticador como Google Authenticator.'
      }
    ]
  };

  const tutorials = [
    {
      title: 'Primeiros Passos no Vibecode',
      duration: '5:30',
      description: 'Aprenda a configurar sua conta e criar seu primeiro projeto',
      thumbnail: 'https://readdy.ai/api/search-image?query=Professional%20tutorial%20video%20thumbnail%20showing%20project%20management%20dashboard%20interface%2C%20clean%20modern%20design%2C%20play%20button%20overlay%2C%20educational%20content%2C%20blue%20and%20white%20color%20scheme&width=300&height=200&seq=tutorial-1&orientation=landscape'
    },
    {
      title: 'Gerenciando Tarefas e Equipes',
      duration: '8:15',
      description: 'Como criar tarefas, atribuir responsáveis e acompanhar o progresso',
      thumbnail: 'https://readdy.ai/api/search-image?query=Task%20management%20tutorial%20video%20thumbnail%2C%20team%20collaboration%20interface%2C%20task%20boards%20and%20assignments%2C%20professional%20educational%20design%2C%20modern%20UI%20elements&width=300&height=200&seq=tutorial-2&orientation=landscape'
    },
    {
      title: 'Relatórios e Métricas',
      duration: '6:45',
      description: 'Entenda como gerar relatórios e analisar o desempenho dos projetos',
      thumbnail: 'https://readdy.ai/api/search-image?query=Analytics%20and%20reporting%20tutorial%20thumbnail%2C%20charts%20and%20graphs%20interface%2C%20business%20intelligence%20dashboard%2C%20professional%20educational%20content%2C%20data%20visualization&width=300&height=200&seq=tutorial-3&orientation=landscape'
    },
    {
      title: 'Configurações Avançadas',
      duration: '10:20',
      description: 'Personalize o Vibecode para suas necessidades específicas',
      thumbnail: 'https://readdy.ai/api/search-image?query=Advanced%20settings%20tutorial%20thumbnail%2C%20configuration%20interface%2C%20customization%20options%2C%20professional%20software%20tutorial%20design%2C%20settings%20dashboard&width=300&height=200&seq=tutorial-4&orientation=landscape'
    }
  ];

  const filteredFaqs = faqs[activeCategory].filter(faq =>
    faq.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleContactSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    const formData = new FormData();
    formData.append('name', contactForm.name);
    formData.append('email', contactForm.email);
    formData.append('subject', contactForm.subject);
    formData.append('message', contactForm.message);

    try {
      const response = await fetch('https://readdy.ai/api/form/d3orfi90qh8759vn7a3g', {
        method: 'POST',
        body: formData
      });

      if (response.ok) {
        alert('Mensagem enviada com sucesso! Entraremos em contato em breve.');
        setContactForm({ name: '', email: '', subject: '', message: '' });
      } else {
        alert('Erro ao enviar mensagem. Tente novamente.');
      }
    } catch (error) {
      alert('Erro ao enviar mensagem. Verifique sua conexão e tente novamente.');
    }
  };

  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "name": "Central de Ajuda - Vibecode",
    "description": "Encontre respostas para suas dúvidas, assista tutoriais e entre em contato conosco. Central de ajuda completa do Vibecode.",
    "url": `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/help`,
    "mainEntity": faqs[activeCategory].map((faq, index) => ({
      "@type": "Question",
      "name": faq.question,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.answer
      }
    }))
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <SEOHead
        title="Central de Ajuda - Vibecode"
        description="Encontre respostas para suas dúvidas, assista tutoriais e entre em contato conosco. Central de ajuda completa do Vibecode."
        keywords="ajuda, suporte, FAQ, tutoriais, Vibecode, dúvidas"
        canonical={`${import.meta.env.VITE_SITE_URL || 'https://example.com'}/help`}
        jsonLd={jsonLd}
      />

      {/* Header */}
      <header className="bg-white shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center">
              <h1 className="text-2xl font-bold text-indigo-600" style={{ fontFamily: "Pacifico, serif" }}>
                Vibecode
              </h1>
            </div>
            <nav className="hidden md:flex space-x-8">
              <a 
                onClick={() => navigate('/dashboard')}
                className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer"
              >
                Dashboard
              </a>
              <a 
                onClick={() => navigate('/reports')}
                className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer"
              >
                Relatórios
              </a>
              <a 
                onClick={() => navigate('/profile')}
                className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer"
              >
                Perfil
              </a>
              <a href="#" className="text-indigo-600 font-medium cursor-pointer">Ajuda</a>
              <a 
                onClick={() => navigate('/')}
                className="text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer"
              >
                Início
              </a>
            </nav>
            <div className="flex items-center space-x-4">
              <button className="p-2 text-gray-400 hover:text-gray-600 cursor-pointer">
                <i className="ri-notification-line text-xl"></i>
              </button>
              <div className="w-8 h-8 bg-indigo-600 rounded-full flex items-center justify-center">
                <span className="text-white text-sm font-medium">JS</span>
              </div>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Page Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-gray-900 mb-4">Central de Ajuda</h1>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Encontre respostas para suas dúvidas, assista tutoriais e entre em contato conosco
          </p>
        </div>

        {/* Search Bar */}
        <div className="max-w-2xl mx-auto mb-12">
          <div className="relative">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder="Pesquisar na central de ajuda..."
              className="w-full px-4 py-3 pl-12 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
            />
            <i className="ri-search-line absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400"></i>
          </div>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* FAQ Section */}
          <div className="lg:col-span-2">
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Perguntas Frequentes</h2>
              
              {/* FAQ Categories */}
              <div className="flex flex-wrap gap-2 mb-6">
                {[
                  { key: 'geral', label: 'Geral' },
                  { key: 'projetos', label: 'Projetos' },
                  { key: 'tarefas', label: 'Tarefas' },
                  { key: 'conta', label: 'Conta' }
                ].map((category) => (
                  <button
                    key={category.key}
                    onClick={() => setActiveCategory(category.key as any)}
                    className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap cursor-pointer ${
                      activeCategory === category.key
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    {category.label}
                  </button>
                ))}
              </div>

              {/* FAQ Items */}
              <div className="space-y-4">
                {filteredFaqs.map((faq, index) => (
                  <details key={index} className="border border-gray-200 rounded-lg">
                    <summary className="p-4 cursor-pointer hover:bg-gray-50 font-medium text-gray-900">
                      {faq.question}
                    </summary>
                    <div className="p-4 pt-0 text-gray-600">
                      {faq.answer}
                    </div>
                  </details>
                ))}
              </div>

              {filteredFaqs.length === 0 && (
                <div className="text-center py-8">
                  <i className="ri-search-line text-4xl text-gray-300 mb-4"></i>
                  <p className="text-gray-600">Nenhuma pergunta encontrada para "{searchQuery}"</p>
                </div>
              )}
            </div>
          </div>

          {/* Sidebar */}
          <div className="space-y-8">
            {/* Video Tutorials */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Tutoriais em Vídeo</h3>
              <div className="space-y-4">
                {tutorials.map((tutorial, index) => (
                  <div key={index} className="flex space-x-3 p-3 hover:bg-gray-50 rounded-lg cursor-pointer transition-colors">
                    <img
                      src={tutorial.thumbnail}
                      alt={tutorial.title}
                      className="w-16 h-12 object-cover rounded"
                    />
                    <div className="flex-1">
                      <h4 className="font-medium text-gray-900 text-sm line-clamp-2">
                        {tutorial.title}
                      </h4>
                      <p className="text-xs text-gray-600 mt-1">{tutorial.duration}</p>
                    </div>
                  </div>
                ))}
              </div>
              <button className="w-full mt-4 text-indigo-600 hover:text-indigo-700 font-medium text-sm cursor-pointer">
                Ver Todos os Tutoriais
              </button>
            </div>

            {/* Quick Links */}
            <div className="bg-white rounded-lg shadow-sm p-6">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Links Rápidos</h3>
              <div className="space-y-3">
                <a href="#" className="flex items-center text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer">
                  <i className="ri-book-line mr-3 text-lg"></i>
                  Documentação Completa
                </a>
                <a href="#" className="flex items-center text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer">
                  <i className="ri-download-line mr-3 text-lg"></i>
                  Downloads e Recursos
                </a>
                <a href="#" className="flex items-center text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer">
                  <i className="ri-community-line mr-3 text-lg"></i>
                  Comunidade de Usuários
                </a>
                <a href="#" className="flex items-center text-gray-700 hover:text-indigo-600 transition-colors cursor-pointer">
                  <i className="ri-feedback-line mr-3 text-lg"></i>
                  Enviar Feedback
                </a>
              </div>
            </div>

            {/* Contact Support */}
            <div className="bg-indigo-50 rounded-lg p-6">
              <h3 className="text-lg font-bold text-indigo-900 mb-2">Precisa de Mais Ajuda?</h3>
              <p className="text-indigo-700 text-sm mb-4">
                Nossa equipe de suporte está pronta para ajudar você
              </p>
              <button
                onClick={() => document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth' })}
                className="w-full bg-indigo-600 text-white py-2 rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer"
              >
                Entrar em Contato
              </button>
            </div>
          </div>
        </div>

        {/* Contact Form */}
        <div id="contact-form" className="mt-16">
          <div className="bg-white rounded-lg shadow-sm p-8">
            <div className="max-w-2xl mx-auto">
              <h2 className="text-2xl font-bold text-gray-900 mb-6 text-center">Entre em Contato</h2>
              <p className="text-gray-600 text-center mb-8">
                Não encontrou a resposta que procurava? Envie sua dúvida e nossa equipe responderá em breve.
              </p>
              
              <form onSubmit={handleContactSubmit} className="space-y-6" data-readdy-form>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Nome Completo *
                    </label>
                    <input
                      type="text"
                      name="name"
                      value={contactForm.name}
                      onChange={(e) => setContactForm(prev => ({ ...prev, name: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      placeholder="Seu nome completo"
                    />
                  </div>
                  
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Email *
                    </label>
                    <input
                      type="email"
                      name="email"
                      value={contactForm.email}
                      onChange={(e) => setContactForm(prev => ({ ...prev, email: e.target.value }))}
                      required
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                      placeholder="seu@email.com"
                    />
                  </div>
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Assunto *
                  </label>
                  <input
                    type="text"
                    name="subject"
                    value={contactForm.subject}
                    onChange={(e) => setContactForm(prev => ({ ...prev, subject: e.target.value }))}
                    required
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    placeholder="Qual é o assunto da sua mensagem?"
                  />
                </div>
                
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Mensagem *
                  </label>
                  <textarea
                    name="message"
                    value={contactForm.message}
                    onChange={(e) => setContactForm(prev => ({ ...prev, message: e.target.value }))}
                    required
                    maxLength={500}
                    rows={5}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    placeholder="Descreva sua dúvida ou problema em detalhes..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {contactForm.message.length}/500 caracteres
                  </p>
                </div>
                
                <div className="text-center">
                  <button
                    type="submit"
                    className="bg-indigo-600 text-white px-8 py-3 rounded-lg hover:bg-indigo-700 transition-colors whitespace-nowrap cursor-pointer"
                  >
                    Enviar Mensagem
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
